import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth, pathForView, pickPostLoginPath } from "@/context/AuthContext";
import InactiveAccountSplash from "@/components/InactiveAccountSplash";

/**
 * Capability-aware route guard.
 *
 * Props:
 *   - role: "customer" | "provider"  — gate by capability flag (legacy
 *     name retained for callsite stability — maps to is_customer /
 *     is_provider). Multi-role users with the capability PASS the gate
 *     regardless of their other capabilities.
 *   - requireAdmin: boolean — render children (the AccessDenied panel
 *     lives inside the page itself) instead of redirecting when the
 *     user lacks is_admin; admin users render normally.
 *
 * Unauthed users always redirect to /login. Authed users without the
 * required capability redirect to whichever dashboard they CAN reach.
 *
 * Phase 3 §3 — after capability checks pass, gate on user.status. A
 * suspended user lands on `<InactiveAccountSplash>` instead of the
 * shell + page. Auto-covers all 12 protected routes (admin tree,
 * customer tree, provider tree, booking-wizard family,
 * /provider/connect/return).
 */
export default function ProtectedRoute({ children, role, requireAdmin = false }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div
        className="flex min-h-[60vh] items-center justify-center text-muted-foreground"
        data-testid="protected-loading"
      >
        Loading…
      </div>
    );
  }
  if (user === null) {
    // Backlog B.2.x — post-login redirect-back via `?next=` query param
    // (no signed token; we only echo the URL path + search). The Login
    // page reads `?next=` and navigates back after a successful login.
    // For deeper safety the Login page guards against off-origin
    // redirects (only paths starting with `/`).
    const here = `${window.location.pathname}${window.location.search}`;
    const safeHere = here.startsWith("/") ? here : "/";
    return <Navigate to={`/login?next=${encodeURIComponent(safeHere)}`} replace />;
  }

  // /admin route: leave routing to the page so AccessDenied has the
  // navbar chrome around it.
  if (requireAdmin) {
    if ((user.status || "active") !== "active") return <InactiveAccountSplash />;
    return children;
  }

  if (role) {
    const flag = role === "provider" ? "is_provider" : "is_customer";
    if (!user[flag]) {
      // Send the user to whichever surface they CAN reach. Prefers the
      // highest-priority capability per pickPostLoginPath.
      return <Navigate to={pickPostLoginPath(user) || "/"} replace />;
    }
  }

  // Phase 3 §3 inactive-account splash — the strict backend dep ALREADY
  // 403s every request, but this gate ensures the user sees a clean
  // splash UI instead of a sea of error toasts.
  if ((user.status || "active") !== "active") {
    return <InactiveAccountSplash />;
  }

  return children;
}

// Re-export for downstream consumers that need the helper outside a route.
export { pathForView };
